/**
 * What each day on the date step is worth to the customer, for the booking as
 * it stands.
 *
 * Every day is still *priced* here — surge, plan, Flexible Time, nearby discount
 * and travel fee all inside one total, via `computeBookingPrice`, the function
 * the cart and checkout use. What changed is what the calendar prints: the
 * saving on the days that have one, rather than a dollar total on all thirty.
 * A month of totals is thirty numbers to compare before you can pick a Tuesday.
 *
 * The obvious way to print a saving is the trap this module was built around.
 * The calendar once marked cheaper days with the nearby discount alone, and a
 * Sunday with a nearby cleaning showed "−$14.80" in green while the Sunday surge
 * made it $32 dearer than the plain Wednesday beside it: green said cheaper, the
 * price said otherwise. A badge reading "Up to $20 off" on that Sunday is the
 * same lie in nicer clothes.
 *
 * So a saving is advertised only where it survives the whole total. Two numbers
 * per day: what the day costs at its best arrival, and what it would cost with
 * no nearby discount at all. The difference is the discount on offer; the badge
 * appears only when the discounted day also lands at or below `typicalPrice` —
 * what this booking costs on an ordinary day of the month. A surged day can
 * therefore carry a real discount and still, correctly, show nothing.
 */

import type { ProximityTier } from "@homero/pricing";
import { BookingPriceState, computeBookingPrice } from "./price";
import { AreaCalendar, AreaDay, arrivalEarns } from "./proximity";

/**
 * The tier a day gives this arrival: the travel fee on a quiet day, the nearby
 * discount on a busy one if the arrival earns it, nothing otherwise.
 */
export function tierForDay(day: AreaDay | undefined, arrival: string): ProximityTier {
  if (!day) return "none";
  if (day.tier === "travel") return "travel";
  if (day.tier === "cluster") {
    // No arrival picked yet reads as Flexible Time, the app's default.
    return arrivalEarns(arrival || "flexible", day.windows) ? "cluster" : "none";
  }
  return "none";
}

/** The percentage the calendar published for a tier; 0 for none. */
export function percentForTier(calendar: AreaCalendar | null, tier: ProximityTier): number {
  if (calendar?.status !== "served") return 0;
  if (tier === "cluster") return calendar.clusterPercent ?? 0;
  if (tier === "travel") return calendar.travelPercent ?? 0;
  return 0;
}

/**
 * The booking's total on one date, in cents, with the tier the area calendar
 * gives it. Null when the engine cannot price that date (a config gap), so the
 * tile shows no number rather than a wrong one.
 */
export function priceOnDay(
  booking: BookingPriceState,
  date: string,
  arrival: string,
  calendar: AreaCalendar | null
): number | null {
  const days = calendar?.status === "served" ? calendar.days : {};
  const tier = tierForDay(days[date], arrival);
  try {
    return computeBookingPrice({
      ...booking,
      cleanDate: date,
      proximityTier: tier,
      proximityPercent: percentForTier(calendar, tier),
      // Priced here, for this date: not the stored quote's question.
      proximityFor: undefined,
      sameDay: false,
    }).totalCents;
  } catch {
    return null;
  }
}

/**
 * The best a date can do, and what it costs with no nearby discount — the two
 * numbers a badge is made of.
 *
 * Priced at Flexible Time rather than at whatever arrival is currently selected,
 * because the badge answers "is there anything for me here?" before the customer
 * has chosen a time. Flexible is the arrival that earns the cluster wherever the
 * day has one (`blocksFor` spans every block), so it is the day's best case —
 * the "max available discount" the calendar is advertising. The exact figure for
 * the arrival they go on to pick is on the time slots and in the cart.
 *
 * Null on either side when the engine cannot price that date, so a day with a
 * config gap shows nothing rather than a saving computed from one good half.
 */
export function savingOnDay(
  booking: BookingPriceState,
  date: string,
  calendar: AreaCalendar | null
): { best: number; saving: number } | null {
  const tier = tierForDay(
    calendar?.status === "served" ? calendar.days[date] : undefined,
    "flexible"
  );
  let priced;
  try {
    priced = computeBookingPrice({
      ...booking,
      cleanDate: date,
      proximityTier: tier,
      proximityPercent: percentForTier(calendar, tier),
      proximityFor: undefined,
      sameDay: false,
    });
  } catch {
    return null;
  }
  /**
   * The advertised amount is the engine's own `proximityDiscountCents` — the
   * figure the cart prints as its `AREA` line and the arrival slots print
   * beside each time. It is pre-tax, so it is a little smaller than the
   * amount the total actually falls by, and that is the direction to err in:
   * three surfaces saying the same number beats a badge that is technically
   * more accurate and looks, to a customer comparing them, like a discrepancy.
   *
   * `best` is the full total, and stays the full total, because that is what
   * `badgeSavings` gates on — see the module header for why a saving is not
   * enough on its own to earn a badge.
   */
  return { best: priced.totalCents, saving: Math.max(0, priced.proximityDiscountCents) };
}

/**
 * What this booking costs on an ordinary day of the month: the most common
 * total, and the median when no total repeats.
 *
 * The mode rather than the minimum or the mean. Most days of a month are the
 * same plain price, and that is exactly what a customer means by "normal" — it
 * is the number they would have paid if they had not gone looking. The mean is
 * dragged up by two surged Sundays, and the minimum would make the normal price
 * look like a discount.
 */
export function typicalPrice(prices: Record<string, number | null>): number | null {
  const values = Object.values(prices).filter(
    (cents): cents is number => cents !== null
  );
  if (values.length === 0) return null;

  const counts = new Map<number, number>();
  values.forEach((cents) => counts.set(cents, (counts.get(cents) ?? 0) + 1));

  let mode: number | null = null;
  let best = 1;
  counts.forEach((count, cents) => {
    // `>` and not `>=`, so a tie keeps the lower price: on a tie the cautious
    // baseline is the smaller one, which advertises fewer savings, not more.
    if (count > best || (count === best && mode !== null && cents < mode)) {
      best = count;
      mode = cents;
    }
  });
  if (mode !== null) return mode;

  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.floor((sorted.length - 1) / 2)];
}

/**
 * The saving to advertise on each day, in cents, split by how loudly.
 *
 * `green`: the day offers a discount *and* ends up at or below the ordinary
 * price. That second clause is what stops a surged Sunday with a nearby
 * cleaning from wearing a green badge while costing more than every day
 * around it.
 *
 * `grey`: the day offers a discount that does not survive its surge. The
 * discount is real — the time slots print it and checkout charges it — so the
 * calendar shows it too, but in grey rather than green, because the day is not
 * cheaper. The surge itself is never named: the customer does not want it
 * shown anywhere.
 */
export function daySavingBadges(
  booking: BookingPriceState,
  dates: string[],
  calendar: AreaCalendar | null
): { green: Record<string, number>; grey: Record<string, number> } {
  const best: Record<string, number | null> = {};
  const savings: Record<string, number> = {};
  dates.forEach((date) => {
    const day = savingOnDay(booking, date, calendar);
    best[date] = day?.best ?? null;
    /**
     * A dollar is the floor, because `tileSaving` rounds down to whole dollars
     * and a 40c saving would print as "$0 off" — a badge that draws the eye to
     * nothing. Below a dollar there is no incentive worth advertising anyway.
     */
    if (day && day.saving >= 100) savings[date] = day.saving;
  });

  const typical = typicalPrice(best);
  if (typical === null) return { green: {}, grey: {} };

  const green: Record<string, number> = {};
  const grey: Record<string, number> = {};
  Object.entries(savings).forEach(([date, saving]) => {
    const total = best[date];
    if (total === null) return;
    if (total <= typical) green[date] = saving;
    else grey[date] = saving;
  });
  return { green, grey };
}

/** The green badges alone: days whose discount leaves them no dearer than normal. */
export function badgeSavings(
  booking: BookingPriceState,
  dates: string[],
  calendar: AreaCalendar | null
): Record<string, number> {
  return daySavingBadges(booking, dates, calendar).green;
}

/**
 * The days to mark green: those at the lowest price. None when every day costs
 * the same — "cheapest" means nothing then, and a month of green reads as noise.
 */
export function cheapestDays(prices: Record<string, number | null>): Set<string> {
  const known = Object.entries(prices).filter(
    (entry): entry is [string, number] => entry[1] !== null
  );
  if (known.length === 0) return new Set();
  const values = known.map(([, cents]) => cents);
  const min = Math.min(...values);
  if (min === Math.max(...values)) return new Set();
  return new Set(known.filter(([, cents]) => cents === min).map(([date]) => date));
}

/** "$183" for 18247¢: a tile shows whole dollars, the summary shows the cents. */
export function tileDollars(cents: number): string {
  return `$${Math.round(cents / 100).toLocaleString("en-US")}`;
}

/**
 * A saving as a tile prints it: "$13" for 1390¢.
 *
 * Rounded *down*, unlike `tileDollars`. A badge is a promise about a number the
 * customer has not seen yet, so $13.90 becomes "$13" and the cart then beats it.
 * `tileDollars` would round that to "$14" and have the cart come in a dollar
 * under what the calendar advertised — the one direction that reads as a bait.
 *
 * The word "off" is the tile's, not this function's: a phone-width tile drops it
 * for want of 5px and keeps the amount.
 */
export function tileSaving(cents: number): string {
  return `$${Math.floor(cents / 100).toLocaleString("en-US")}`;
}
